import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';

const CreateDiscount = ({ onDiscountCreated }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    platform: 'Steam',
    discountPercentage: '',
    gameLink: '',
    endDate: '',
    customGameData: {
      name: '',
      description: '',
      imageUrl: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const platforms = [
    'Steam',
    'Epic Games',
    'GOG',
    'Fanatical',
    'Green Man Gaming',
    'Humble Bundle',
    'PlayStation',
    'Xbox',
    'Nintendo',
    'Origin',
    'Ubisoft Store',
    'Battle.net',
    'itch.io',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('custom.')) {
      const customField = name.split('.')[1];
      setFormData({
        ...formData,
        customGameData: {
          ...formData.customGameData,
          [customField]: value
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      // Validate form
      if (!formData.platform || !formData.discountPercentage || !formData.gameLink || !formData.endDate) {
        throw new Error('Please fill in all required fields');
      }

      const discountPercentage = parseFloat(formData.discountPercentage);
      if (isNaN(discountPercentage) || discountPercentage < 0 || discountPercentage > 100) {
        throw new Error('Discount percentage must be between 0 and 100');
      }

      // Parse the datetime-local value correctly
      // formData.endDate is like "2025-10-29T22:30"
      // We need to treat this as the user's intended time and convert to UTC properly
      const [datePart, timePart] = formData.endDate.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);
      
      // Create date in user's local timezone
      const endDate = new Date(year, month - 1, day, hour, minute);
      
      if (endDate <= new Date()) {
        throw new Error('End date must be in the future');
      }

      // Prepare data to send
      const dataToSend = {
        platform: formData.platform,
        discountPercentage: discountPercentage,
        gameLink: formData.gameLink,
        endDate: endDate.toISOString() // Now this will properly convert local time to UTC
      };

      // For non-Steam platforms or if custom data is provided
      if (formData.platform !== 'Steam' || formData.customGameData.name) {
        dataToSend.customGameData = formData.customGameData;
      }

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/discounts', dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSuccess(true);
      
      // Reset form
      setFormData({
        platform: 'Steam',
        discountPercentage: '',
        gameLink: '',
        endDate: '',
        customGameData: {
          name: '',
          description: '',
          imageUrl: ''
        }
      });

      // Notify parent component
      if (onDiscountCreated) {
        onDiscountCreated(response.data);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create discount');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="create-discount-container">
        <div className="auth-required">
          <p>Please log in to post a discount</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-discount-container">
      <h2>Post a Game Discount</h2>
      <form onSubmit={handleSubmit} className="discount-form">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">Discount posted successfully!</div>}

        {/* Platform */}
        <div className="form-group">
          <label htmlFor="platform">
            Platform <span className="required">*</span>
          </label>
          <select
            id="platform"
            name="platform"
            value={formData.platform}
            onChange={handleChange}
            required
          >
            {platforms.map((platform) => (
              <option key={platform} value={platform}>
                {platform}
              </option>
            ))}
          </select>
        </div>

        {/* Discount Percentage */}
        <div className="form-group">
          <label htmlFor="discountPercentage">
            Discount Percentage <span className="required">*</span>
          </label>
          <input
            type="number"
            id="discountPercentage"
            name="discountPercentage"
            value={formData.discountPercentage}
            onChange={handleChange}
            min="0"
            max="100"
            placeholder="e.g., 50"
            required
          />
          <small>Enter a number between 0 and 100</small>
        </div>

        {/* Game Link */}
        <div className="form-group">
          <label htmlFor="gameLink">
            Game Link <span className="required">*</span>
          </label>
          <input
            type="url"
            id="gameLink"
            name="gameLink"
            value={formData.gameLink}
            onChange={handleChange}
            placeholder="https://store.steampowered.com/app/..."
            required
          />
          <small>
            {formData.platform === 'Steam'
              ? 'Paste the Steam store link (we\'ll fetch game details automatically)'
              : 'Paste the link to the game'}
          </small>
        </div>

        {/* End Date */}
        <div className="form-group">
          <label htmlFor="endDate">
            Discount End Date <span className="required">*</span>
          </label>
          <input
            type="datetime-local"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            min={new Date().toISOString().slice(0, 16)}
            required
          />
          <small>When does this discount expire? (Your local time: {Intl.DateTimeFormat().resolvedOptions().timeZone})</small>
        </div>

        {/* Custom Game Data (for non-Steam or fallback) */}
        {formData.platform !== 'Steam' && (
          <div className="custom-game-section">
            <h3>Game Details (Optional - Auto-fetch enabled)</h3>
            <p className="section-description">
              We'll try to automatically fetch game data based on the URL or game name. 
              You can provide details manually if auto-fetch doesn't work.
            </p>

            <div className="form-group">
              <label htmlFor="custom.name">
                Game Name {!formData.customGameData.name && <span className="optional-label">(Optional - we'll try to detect it)</span>}
              </label>
              <input
                type="text"
                id="custom.name"
                name="custom.name"
                value={formData.customGameData.name}
                onChange={handleChange}
                placeholder="Enter game name if auto-detection fails"
              />
              <small>Provide the game name to help us find the right game data</small>
            </div>

            <div className="form-group">
              <label htmlFor="custom.description">Description</label>
              <textarea
                id="custom.description"
                name="custom.description"
                value={formData.customGameData.description}
                onChange={handleChange}
                placeholder="Brief game description (optional)"
                rows="3"
              />
              <small>Provide a brief description of the game (don't include "Description:" prefix)</small>
            </div>

            <div className="form-group">
              <label htmlFor="custom.imageUrl">Image URL</label>
              <input
                type="url"
                id="custom.imageUrl"
                name="custom.imageUrl"
                value={formData.customGameData.imageUrl}
                onChange={handleChange}
                placeholder="https://example.com/game-image.jpg (optional)"
              />
            </div>
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-submit" disabled={loading}>
          {loading ? 'Posting...' : 'Post Discount'}
        </button>
      </form>

      <style jsx>{`
        .create-discount-container {
          background: var(--card-bg);
          border-radius: 12px;
          padding: 24px;
          box-shadow: var(--shadow);
          margin-bottom: 24px;
          border: 1px solid var(--border-color);
        }

        .auth-required {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary);
        }

        h2 {
          margin-top: 0;
          color: var(--text-primary);
          font-size: 1.8em;
          margin-bottom: 24px;
        }

        h3 {
          color: var(--text-primary);
          font-size: 1.2em;
          margin-bottom: 12px;
        }

        .section-description {
          color: var(--text-secondary);
          font-size: 0.9em;
          margin-bottom: 16px;
        }

        .discount-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        label {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 0.95em;
        }

        .required {
          color: var(--accent-red);
        }

        .optional-label {
          color: var(--text-secondary);
          font-weight: normal;
          font-size: 0.85em;
          font-style: italic;
        }

        input,
        select,
        textarea {
          padding: 12px;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          font-size: 1em;
          transition: border-color 0.2s;
          background: var(--card-bg);
          color: var(--text-primary);
        }

        input:focus,
        select:focus,
        textarea:focus {
          outline: none;
          border-color: var(--btn-primary);
        }

        small {
          color: var(--text-secondary);
          font-size: 0.85em;
        }

        .custom-game-section {
          background: var(--card-bg-secondary);
          padding: 20px;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          font-weight: 500;
        }

        .alert-error {
          background: var(--error-bg);
          color: var(--error-text);
          border: 1px solid var(--error-text);
        }

        .alert-success {
          background: var(--success-bg);
          color: var(--success-text);
          border: 1px solid var(--success-text);
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 1em;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--btn-primary);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--btn-primary-hover);
        }

        .btn-primary:disabled {
          background: var(--btn-secondary);
          cursor: not-allowed;
          opacity: 0.6;
        }

        .btn-submit {
          margin-top: 8px;
        }
      `}</style>
    </div>
  );
};

export default CreateDiscount;
